import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, inr } from "@/lib/api";
import RatingModal from "@/components/RatingModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Send, Volume2, MapPin, MessageCircle, ShieldAlert, Loader2, Star, ArrowLeft, Loader,
} from "lucide-react";

function MessageBubble({ m, onSpeak, speakingId }) {
  const mine = m.is_mine;
  const isAddress = m.kind === "address";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
      <div className={`group max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed relative ${
          isAddress ? "bg-crimson/10 text-crimson-700 border border-crimson/20 rounded-bl-sm"
            : mine ? "bg-brand text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
        }`}>
          {isAddress && <MapPin size={14} className="inline mr-1.5 -mt-0.5" />}
          {m.text}
        </div>
        <button
          onClick={() => onSpeak(m)}
          className={`text-[11px] flex items-center gap-1 text-slate-400 hover:text-brand transition-colors ${mine ? "self-end" : "self-start"}`}
          data-testid={`tts-btn-${m.id}`}
        >
          {speakingId === m.id ? <Loader size={12} className="animate-spin" /> : <Volume2 size={12} />} Read aloud
        </button>
      </div>
    </div>
  );
}

export default function Chat() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const activeId = params.get("c");

  const [conversations, setConversations] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [speakingId, setSpeakingId] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [ratingTxn, setRatingTxn] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const endRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (ready && !user) navigate("/");
  }, [ready, user, navigate]);

  const loadConversations = useCallback(() => {
    api.get("/conversations").then(({ data }) => setConversations(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    loadConversations();
    api.get("/addresses").then(({ data }) => setAddresses(data));
    api.get("/transactions").then(({ data }) => setTransactions([...data.purchases, ...data.sales]));
  }, [user, loadConversations]);

  // load + poll active thread
  useEffect(() => {
    if (!activeId) { setThread(null); setMessages([]); return; }
    let cancel = false;
    const fetchThread = async (showLoader) => {
      if (showLoader) setLoadingThread(true);
      try {
        const { data } = await api.get(`/conversations/${activeId}`);
        if (cancel) return;
        setThread(data.conversation);
        setMessages(data.messages);
        if (showLoader) loadConversations();
      } catch (e) { /* ignore */ }
      finally { if (showLoader) setLoadingThread(false); }
    };
    fetchThread(true);
    const iv = setInterval(() => fetchThread(false), 3500);
    return () => { cancel = true; clearInterval(iv); };
  }, [activeId, loadConversations]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !activeId) return;
    const body = text.trim();
    setText("");
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, { text: body });
      setMessages((prev) => [...prev, data]);
      loadConversations();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setAddress = async (pointId) => {
    try {
      const { data } = await api.post(`/conversations/${activeId}/address`, { pickup_point_id: pointId });
      setMessages((prev) => [...prev, data]);
      const { data: conv } = await api.get(`/conversations/${activeId}`);
      setThread(conv.conversation);
      toast.success("Pickup point shared with the buyer");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const speak = async (m) => {
    setSpeakingId(m.id);
    try {
      const res = await api.post("/tts", { text: m.text }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeakingId(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setSpeakingId(null); fallbackSpeak(m.text); };
      await audio.play();
    } catch (e) {
      fallbackSpeak(m.text);
    }
  };

  const fallbackSpeak = (t) => {
    try {
      const u = new SpeechSynthesisUtterance(t);
      u.onend = () => setSpeakingId(null);
      window.speechSynthesis.speak(u);
    } catch { setSpeakingId(null); }
  };

  const txnForThread = thread && transactions.find(
    (t) => t.listing?.id === thread.listing?.id && t.counterparty?.id === thread.other?.id
  );

  if (!user) return null;

  return (
    <div className="mx-auto max-w-7xl px-4 lg:px-8 py-6 lg:py-8">
      <h1 className="font-display font-extrabold text-ink text-2xl lg:text-3xl tracking-tight mb-4">Messages</h1>

      <div className="grid lg:grid-cols-3 gap-4 h-[calc(100vh-220px)] min-h-[480px]">
        {/* Conversation list */}
        <div className={`lg:col-span-1 bg-white border border-slate-200 rounded-xl overflow-y-auto ${activeId ? "hidden lg:block" : "block"}`} data-testid="conversation-list">
          {conversations.length === 0 ? (
            <div className="text-center text-sm text-slate-400 mt-16 px-6">
              <MessageCircle size={30} className="mx-auto mb-3 text-slate-300" />
              No conversations yet. Start one from any listing in the Marketplace.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {conversations.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/chat?c=${c.id}`)}
                  className={`w-full text-left px-4 py-3.5 flex items-center gap-3 hover:bg-slate-50 transition-colors ${activeId === c.id ? "bg-slate-50" : ""}`}
                  data-testid={`conversation-${c.id}`}
                >
                  <img src={c.listing?.image} alt="" className="h-12 w-12 rounded-lg object-cover bg-slate-100 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-ink text-sm truncate">{c.other?.name}</span>
                      {c.unread > 0
                        ? <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-crimson text-white text-[10px] font-bold grid place-items-center shrink-0" data-testid={`conv-unread-${c.id}`}>{c.unread > 9 ? "9+" : c.unread}</span>
                        : <span className="text-[10px] label-mono text-slate-400">{c.role}</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{c.listing?.title}</div>
                    <div className="text-xs text-slate-400 truncate mt-0.5">{c.last_message || "Say hello 👋"}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Thread */}
        <div className={`lg:col-span-2 bg-[#F8FAFC] border border-slate-200 rounded-xl flex flex-col overflow-hidden ${activeId ? "flex" : "hidden lg:flex"}`}>
          {!thread ? (
            <div className="flex-1 grid place-items-center text-slate-400 text-sm">
              {loadingThread ? <Loader2 className="animate-spin" /> : "Select a conversation"}
            </div>
          ) : (
            <>
              {/* header */}
              <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-3">
                <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => navigate("/chat")} data-testid="chat-back-btn"><ArrowLeft size={18} /></Button>
                <Avatar className="h-10 w-10"><AvatarImage src={thread.other?.avatar} /><AvatarFallback>{thread.other?.name?.[0]}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-ink text-sm truncate">{thread.other?.name}</div>
                  <div className="text-xs text-slate-500 truncate">{thread.listing?.title} · {inr(thread.listing?.price)}</div>
                </div>
                {txnForThread && !txnForThread.i_rated && (
                  <Button size="sm" onClick={() => setRatingTxn(txnForThread)} className="bg-amber-400 hover:bg-amber-500 text-ink" data-testid="chat-rate-btn">
                    <Star size={14} className="mr-1 fill-ink" /> Rate
                  </Button>
                )}
              </div>

              {/* safety banner */}
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-800 flex items-center gap-2">
                <ShieldAlert size={14} className="shrink-0" /> Meet only at verified campus points. Never pay outside the platform.
              </div>

              {/* messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m) => <MessageBubble key={m.id} m={m} onSpeak={speak} speakingId={speakingId} />)}
                <div ref={endRef} />
              </div>

              {/* seller address selector */}
              {thread.role === "seller" && (
                <div className="px-4 py-2.5 border-t border-slate-200 bg-white flex items-center gap-2">
                  <MapPin size={15} className="text-crimson shrink-0" />
                  <Select onValueChange={setAddress} value={thread.pickup_point?.id || ""}>
                    <SelectTrigger className="h-9 text-sm" data-testid="chat-address-select">
                      <SelectValue placeholder="Set a pickup / drop-off point for this sale" />
                    </SelectTrigger>
                    <SelectContent>
                      {addresses.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* composer */}
              <div className="p-3 border-t border-slate-200 bg-white flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="h-11" data-testid="chat-input" />
                <Button onClick={send} className="h-11 px-4 bg-ink hover:bg-slate-800 text-white" data-testid="chat-send-btn"><Send size={17} /></Button>
              </div>
            </>
          )}
        </div>
      </div>

      <RatingModal open={!!ratingTxn} onOpenChange={(v) => !v && setRatingTxn(null)} transaction={ratingTxn}
        onRated={() => { setRatingTxn(null); api.get("/transactions").then(({ data }) => setTransactions([...data.purchases, ...data.sales])); }} />
    </div>
  );
}
